<template>
  <div>
    <!-- Header -->
    <v-card class="pa-3" color="#0d1520" dark>
      <v-row align="center">
        <v-col cols="auto">
          <div class="text-h6 font-weight-bold" style="color: #d4a843;">
            OfertaPro
          </div>
          <div class="text-caption" style="opacity: 0.6;">
            Sistema de gestión de ofertas y licitaciones
          </div>
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <v-chip small color="#1e3f7a" dark class="mr-2">
            {{ licitaciones.length }} Licitaciones
          </v-chip>
          <v-chip small color="#27ae60" dark class="mr-2">
            {{ licitacionesActivas }} En curso
          </v-chip>
          <v-chip small color="#d4a843" dark>
            {{ oferentes.length }} Oferentes
          </v-chip>
        </v-col>
      </v-row>
    </v-card>

    <!-- Acceso a Costos Estándares -->
    <v-card class="mt-2 pa-3" elevation="1">
      <v-btn outlined color="#1e3f7a" @click="$router.push({ name: 'CostosEstandares' })">
        <v-icon left>mdi-book-open-variant</v-icon>Costos Estándares para Presupuestación
      </v-btn>
    </v-card>

    <!-- Sección Licitaciones -->
    <v-card class="mt-2 pa-3" elevation="1">
      <div class="d-flex align-center mb-3">
        <div class="text-subtitle-1 font-weight-bold">📋 Licitaciones / Proyectos</div>
        <v-spacer />
        <v-select
          v-model="filtroEstado"
          :items="estadosFilter"
          label="Estado"
          dense outlined hide-details
          style="max-width: 180px;"
          class="mr-2"
          clearable
        />
        <v-btn color="#d4a843" dark small @click="abrirModalLic(null)">
          <v-icon left small>mdi-plus</v-icon>Nueva Licitación
        </v-btn>
      </div>

      <v-row dense>
        <v-col v-for="lic in licitacionesFiltradas" :key="lic.id" cols="12" sm="6" md="4">
          <v-card outlined class="lic-card" @click="abrirLicitacion(lic.id)">
            <div class="lic-stripe" :class="stripeClass(lic.estado)"></div>
            <v-card-text class="pa-3">
              <div class="font-weight-bold text-body-2 mb-1">{{ lic.nombre || 'Sin nombre' }}</div>
              <div class="text-caption grey--text mb-2" style="max-height: 32px; overflow: hidden;">
                {{ lic.obra || 'Sin descripción' }}
              </div>
              <div class="d-flex flex-wrap" style="gap: 4px;">
                <v-chip x-small outlined color="#d4a843">{{ lic.comitente || 'Sin comitente' }}</v-chip>
                <v-chip x-small :color="estadoColor(lic.estado)" dark>{{ lic.estado }}</v-chip>
                <v-chip x-small outlined v-if="lic.presupuestoOficial">
                  PO: ${{ formatMonto(lic.presupuestoOficial) }}
                </v-chip>
              </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-2">
              <span class="text-caption grey--text">
                {{ lic.fecha ? 'Apertura: ' + formatFecha(lic.fecha) : 'Sin fecha' }}
              </span>
              <v-spacer />
              <v-btn x-small color="#d4a843" dark @click.stop="abrirLicitacion(lic.id)">
                <v-icon x-small left>mdi-play</v-icon>Abrir
              </v-btn>
              <v-btn x-small outlined @click.stop="duplicarLic(lic.id)">
                <v-icon x-small>mdi-content-copy</v-icon>
              </v-btn>
              <v-btn x-small color="red" dark @click.stop="eliminarLic(lic)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <!-- Sección Oferentes -->
    <v-card class="mt-2 pa-3" elevation="1">
      <div class="d-flex align-center mb-3">
        <div class="text-subtitle-1 font-weight-bold">👥 Oferentes Registrados</div>
        <v-spacer />
        <v-btn outlined small @click="abrirModalOf(null)">
          <v-icon left small>mdi-plus</v-icon>Agregar Oferente
        </v-btn>
      </div>

      <v-row dense>
        <v-col v-for="of in oferentes" :key="of.id" cols="12" sm="6" md="4">
          <v-card outlined class="pa-3">
            <div class="d-flex align-center" style="gap: 10px;">
              <v-avatar v-if="of.logo" size="40" tile>
                <v-img :src="of.logo" contain />
              </v-avatar>
              <v-icon v-else large color="grey">mdi-domain</v-icon>
              <div style="flex: 1; min-width: 0;">
                <div class="font-weight-bold text-body-2">{{ of.nombre }}</div>
                <div class="text-caption grey--text">{{ of.cuit }} {{ of.domicilio ? '· ' + of.domicilio : '' }}</div>
              </div>
            </div>
            <v-card-actions class="pa-0 mt-2">
              <span class="text-caption grey--text">{{ of.email }}</span>
              <v-spacer />
              <v-btn x-small outlined @click="abrirModalOf(of)">
                <v-icon x-small left>mdi-pencil</v-icon>Editar
              </v-btn>
              <v-btn x-small color="red" dark @click="eliminarOf(of)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <!-- Modal Licitación -->
    <v-dialog v-model="dialogLic" max-width="600" persistent>
      <v-card>
        <v-toolbar dense dark color="#1e3f7a">
          <v-toolbar-title class="text-body-2 font-weight-bold">
            {{ editLic ? 'Editar Licitación' : 'Nueva Licitación' }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon small @click="dialogLic = false"><v-icon small>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pa-4">
          <v-text-field dense outlined v-model="formLic.nombre" label="Nombre / Referencia interna" />
          <v-text-field dense outlined v-model="formLic.comitente" label="Comitente" />
          <v-textarea dense outlined v-model="formLic.obra" label="Descripción de la Obra" rows="2" auto-grow />
          <v-row dense>
            <v-col cols="6">
              <v-select dense outlined v-model="formLic.estado" :items="estados" label="Estado" />
            </v-col>
            <v-col cols="6">
              <v-text-field dense outlined v-model.number="formLic.presupuestoOficial" label="Presupuesto Oficial ($)" type="number" />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="6">
              <v-text-field dense outlined v-model="formLic.numero" label="N° Licitación" />
            </v-col>
            <v-col cols="6">
              <v-text-field dense outlined v-model="formLic.fecha" label="Fecha Apertura" type="date" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text @click="dialogLic = false">Cancelar</v-btn>
          <v-btn color="#d4a843" dark @click="guardarLic" :loading="guardandoLic">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Oferente -->
    <v-dialog v-model="dialogOf" max-width="550" persistent>
      <v-card>
        <v-toolbar dense dark color="#1e3f7a">
          <v-toolbar-title class="text-body-2 font-weight-bold">
            {{ editOf ? 'Editar Oferente' : 'Nuevo Oferente' }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon small @click="dialogOf = false"><v-icon small>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pa-4">
          <v-row dense>
            <v-col cols="7">
              <v-text-field dense outlined v-model="formOf.nombre" label="Razón Social" />
            </v-col>
            <v-col cols="5">
              <v-text-field dense outlined v-model="formOf.cuit" label="CUIT" />
            </v-col>
          </v-row>
          <v-text-field dense outlined v-model="formOf.domicilio" label="Domicilio" />
          <v-row dense>
            <v-col cols="6">
              <v-text-field dense outlined v-model="formOf.telefono" label="Teléfono" />
            </v-col>
            <v-col cols="6">
              <v-text-field dense outlined v-model="formOf.email" label="Email" />
            </v-col>
          </v-row>
          <v-text-field dense outlined v-model="formOf.representante" label="Representante Legal" />
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text @click="dialogOf = false">Cancelar</v-btn>
          <v-btn color="#d4a843" dark @click="guardarOf" :loading="guardandoOf">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Axios from "../../axios-auth";

export default {
  name: "OfertasIndex",

  data() {
    return {
      licitaciones: [],
      oferentes: [],
      filtroEstado: null,
      estados: ["En preparación", "Presentada", "Adjudicada", "En ejecución", "Finalizada"],
      estadosFilter: ["En preparación", "Presentada", "Adjudicada", "En ejecución", "Finalizada"],
      // Modal Licitación
      dialogLic: false,
      editLic: null,
      guardandoLic: false,
      formLic: { nombre: "", comitente: "", obra: "", estado: "En preparación", presupuestoOficial: 0, numero: "", fecha: "" },
      // Modal Oferente
      dialogOf: false,
      editOf: null,
      guardandoOf: false,
      formOf: { nombre: "", cuit: "", domicilio: "", telefono: "", email: "", representante: "" },
    };
  },

  computed: {
    licitacionesActivas() {
      return this.licitaciones.filter((l) =>
        ["En preparación", "Presentada", "En ejecución"].includes(l.estado)
      ).length;
    },
    licitacionesFiltradas() {
      if (!this.filtroEstado) return this.licitaciones;
      return this.licitaciones.filter((l) => l.estado === this.filtroEstado);
    },
  },

  mounted() {
    this.cargarLicitaciones();
    this.cargarOferentes();
  },

  methods: {
    cargarLicitaciones() {
      Axios.get("/Oferta/Licitaciones")
        .then((r) => { this.licitaciones = r.data || []; })
        .catch(() => { this.licitaciones = []; });
    },

    cargarOferentes() {
      Axios.get("/Oferta/Oferentes")
        .then((r) => { this.oferentes = r.data || []; })
        .catch(() => { this.oferentes = []; });
    },

    abrirLicitacion(id) {
      this.$router.push({ name: "OfertaEditar", params: { id } });
    },

    abrirModalLic(lic) {
      this.editLic = lic;
      if (lic) {
        this.formLic = {
          nombre: lic.nombre || "",
          comitente: lic.comitente || "",
          obra: lic.obra || "",
          estado: lic.estado || "En preparación",
          presupuestoOficial: lic.presupuestoOficial || 0,
          numero: lic.numero || "",
          fecha: lic.fecha || "",
        };
      } else {
        this.formLic = { nombre: "", comitente: "", obra: "", estado: "En preparación", presupuestoOficial: 0, numero: "", fecha: "" };
      }
      this.dialogLic = true;
    },

    guardarLic() {
      if (!this.formLic.nombre) {
        this.$swal({ icon: "warning", title: "Ingrese un nombre", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
        return;
      }
      this.guardandoLic = true;
      const payload = { ...this.formLic };

      const request = this.editLic
        ? Axios.put(`/Oferta/Licitacion/${this.editLic.id}`, payload)
        : Axios.post("/Oferta/Licitacion", payload);

      request
        .then(() => {
          this.dialogLic = false;
          this.cargarLicitaciones();
          this.$swal({ icon: "success", title: "Licitación guardada", toast: true, position: "top-end", showConfirmButton: false, timer: 2000 });
        })
        .catch(() => {
          this.$swal({ icon: "error", title: "Error al guardar", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
        })
        .finally(() => { this.guardandoLic = false; });
    },

    duplicarLic(id) {
      Axios.post(`/Oferta/Licitacion/${id}/Duplicar`)
        .then(() => {
          this.cargarLicitaciones();
          this.$swal({ icon: "success", title: "Licitación duplicada", toast: true, position: "top-end", showConfirmButton: false, timer: 2000 });
        })
        .catch(() => {});
    },

    eliminarLic(lic) {
      this.$swal({
        title: "¿Eliminar licitación?",
        text: lic.nombre,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      }).then((r) => {
        if (r.value) {
          Axios.delete(`/Oferta/Licitacion/${lic.id}`)
            .then(() => { this.cargarLicitaciones(); })
            .catch(() => {});
        }
      });
    },

    abrirModalOf(of) {
      this.editOf = of;
      if (of) {
        this.formOf = {
          nombre: of.nombre || "",
          cuit: of.cuit || "",
          domicilio: of.domicilio || "",
          telefono: of.telefono || "",
          email: of.email || "",
          representante: of.representante || "",
        };
      } else {
        this.formOf = { nombre: "", cuit: "", domicilio: "", telefono: "", email: "", representante: "" };
      }
      this.dialogOf = true;
    },

    guardarOf() {
      if (!this.formOf.nombre) {
        this.$swal({ icon: "warning", title: "Ingrese la razón social", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
        return;
      }
      this.guardandoOf = true;
      const payload = { ...this.formOf };

      const request = this.editOf
        ? Axios.put(`/Oferta/Oferente/${this.editOf.id}`, payload)
        : Axios.post("/Oferta/Oferente", payload);

      request
        .then(() => {
          this.dialogOf = false;
          this.cargarOferentes();
          this.$swal({ icon: "success", title: "Oferente guardado", toast: true, position: "top-end", showConfirmButton: false, timer: 2000 });
        })
        .catch(() => {
          this.$swal({ icon: "error", title: "Error al guardar", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
        })
        .finally(() => { this.guardandoOf = false; });
    },

    eliminarOf(of) {
      this.$swal({
        title: "¿Eliminar oferente?",
        text: of.nombre,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      }).then((r) => {
        if (r.value) {
          Axios.delete(`/Oferta/Oferente/${of.id}`)
            .then(() => { this.cargarOferentes(); })
            .catch(() => {});
        }
      });
    },

    // Helpers
    formatMonto(n) {
      if (!n) return "0";
      if (n >= 1e9) return (n / 1e9).toFixed(1) + "B";
      if (n >= 1e6) return (n / 1e6).toFixed(0) + "M";
      return (n / 1e3).toFixed(0) + "K";
    },

    formatFecha(d) {
      if (!d) return "";
      try {
        return new Date(d + "T00:00").toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "2-digit" });
      } catch { return d; }
    },

    stripeClass(estado) {
      const map = { Presentada: "stripe-blue", Adjudicada: "stripe-green", "En ejecución": "stripe-green" };
      return map[estado] || "stripe-gold";
    },

    estadoColor(estado) {
      const map = { "En preparación": "#d4a843", Presentada: "#4a7fcc", Adjudicada: "#27ae60", "En ejecución": "#27ae60", Finalizada: "#e5383b" };
      return map[estado] || "grey";
    },
  },
};
</script>

<style scoped>
.lic-card {
  transition: all 0.15s;
  cursor: pointer;
}
.lic-card:hover {
  border-color: #d4a843 !important;
  transform: translateY(-2px);
}
.lic-stripe {
  height: 4px;
}
.stripe-gold { background: linear-gradient(90deg, #d4a843, #f0c96a); }
.stripe-blue { background: linear-gradient(90deg, #1e3f7a, #4a7fcc); }
.stripe-green { background: linear-gradient(90deg, #1e6b4a, #27ae60); }
</style>
